import React, { Dispatch } from "react";
import { RouteComponentProps, Redirect, Switch } from "react-router-dom";
import { connect } from "react-redux";
import { User } from "oidc-client";
import { appPaths, IAppState, ApiRequest, PageTitleSetter, appPageTitles } from "../../Common";
import { ProtectedRoute } from "../../Auth";
import CompanyProfileContainer from "./Profile/CompanyProfileContainer";
import CompanySettingsContainer from "./Settings/CompanySettingsContainer";
import { Role } from "../../Common/Constants/roleEnums";
import CompanyProfileFeatureRequestsContainer from "./Profile/CompanyProfileFeatureRequestsContainer";
import CompanyProfileBugReportsContainer from "./Profile/CompanyProfileBugReportsContainer";
import TopicsListContainer from "../../Topic/Components/TopicsList/TopicsListContainer";
import { setPath, setBreadcrumbs } from "../../Startup/Actions/StartupActions";
import { BreadcrumbData, breadcrumbService } from "../../Layout";
import { breadcrumbsMap } from "../Constants/breadcrumbsMap";
import { endpoints } from "../Constants/endpoints";
import ResponseHandler from "../../App/ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { Company } from "../Models/Company";

type TParams = {
  username: string;
};

type StateProps = {
  path?: string;
  user?: User;
};

type DispatchProps = {
  setPath: (path: string) => void;
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  path: state.app.path,
  user: state.oidc.user
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  setPath: (path: string) => dispatch(setPath(path)),
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => dispatch(setBreadcrumbs(breadcrumbs))
});

class CompanyRoutes extends React.Component<
  RouteComponentProps<TParams> & StateProps & DispatchProps
> {
  variableResolver = async (key: string, variables: Map<string, string>) => {
    switch (key) {
      case ":shortname":
        return this.getCompanyName(variables.get(key));
      default:
        return key;
    }
  };

  getCompanyName = async (shortname?: string) => {
    let name;

    if (shortname && !!this.props.user) {
      await ApiRequest.fromPath(endpoints.companies.getByShortname(shortname))
        .withAuthHeader()
        .get()
        .then(async response => {
          await ResponseHandler.handle(response, (result: ResponseResult<Company>) => {
            ({ name } = result.payload);
          });
        });
    }

    return name;
  };

  setBreadcrumbs = () => {
    if (this.props.path !== this.props.location.pathname) {
      // this.props.setPath(this.props.location.pathname);

      this.props.setBreadcrumbs(
        breadcrumbService.createBreadcrumbs(
          this.props.location.pathname,
          breadcrumbsMap,
          this.variableResolver
        )
      );
    }
  };

  render() {
    this.setBreadcrumbs();

    return (
      <>
        <PageTitleSetter title={appPageTitles.general} />
        <Switch>
          <ProtectedRoute exact path={appPaths.company.topics} component={TopicsListContainer} />
          <ProtectedRoute
            exact
            path={appPaths.company.settings}
            component={CompanySettingsContainer}
            roles={[Role.Admin]}
          />
          <ProtectedRoute exact path={appPaths.company.root} component={CompanyProfileContainer} />
          <ProtectedRoute
            exact
            path={appPaths.company.featureRequests}
            component={CompanyProfileFeatureRequestsContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.company.bugReports}
            component={CompanyProfileBugReportsContainer}
          />
          <Redirect to={appPaths.notFound} />
        </Switch>
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompanyRoutes);
